import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api";
import { useAuth } from "../AuthContext";
import { Shield, Trophy, Sparkles, ArrowRight, Check, Flame, Swords, EyeOff, Settings, Save, Copy, CheckCheck, Flag } from "lucide-react";
import { FaTwitch, FaYoutube, FaTiktok, FaInstagram, FaXTwitter, FaDiscord, FaPlaystation, FaXbox, FaDesktop } from "react-icons/fa6";
import { SiKick } from "react-icons/si";

const SOCIAL_PLATFORMS = [
  { key: "twitch_url", label: "Twitch", testid: "ply-social-twitch", Icon: FaTwitch, color: "#a970ff" },
  { key: "kick_url", label: "Kick", testid: "ply-social-kick", Icon: SiKick, color: "#53fc18" },
  { key: "youtube_url", label: "YouTube", testid: "ply-social-youtube", Icon: FaYoutube, color: "#ff0000" },
  { key: "tiktok_url", label: "TikTok", testid: "ply-social-tiktok", Icon: FaTiktok, color: "#ffffff" },
  { key: "instagram_link", label: "Instagram", testid: "ply-social-instagram", Icon: FaInstagram, color: "#e1306c" },
  { key: "x_link", label: "X", testid: "ply-social-x", Icon: FaXTwitter, color: "#ffffff" },
];

export default function PlayerProfilePage() {
  const { id } = useParams();
  const [u, setU] = useState(null);
  const { user: currentUser } = useAuth();
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("سلوك غير لائق");
  const [reportMessage, setReportMessage] = useState("");
  const [reportSending, setReportSending] = useState(false);

  // إعدادات تخصيص الإنجازات
  const [hiddenIds, setHiddenIds] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [filterMode, setFilterMode] = useState("unlocked"); // 'unlocked' | 'all'
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setU(null);
    setNotFound(false);
    setLoadError(false);
    setIsEditing(false);
    api.get(`/users/${encodeURIComponent(id)}`, { signal: controller.signal, timeout: 12000 }).then(({ data }) => {
      if (controller.signal.aborted) return;
      setU(data);
      setHiddenIds(data.hidden_achievements || []);
    }).catch((error) => {
      if (controller.signal.aborted) return;
      if (error.response?.status === 404) setNotFound(true);
      else setLoadError(true);
    });
    return () => controller.abort();
  }, [id, retry]);

  if (loadError) return <div className="p-12 text-center text-white/60">تعذر تحميل بيانات اللاعب. <button onClick={() => setRetry(n => n + 1)} className="text-gold-400 underline">إعادة المحاولة</button></div>;

  if (notFound) {
    return <div data-testid="player-not-found" className="bg-[#111113]/90 border border-white/10 rounded-2xl p-12 text-center text-white/40 text-sm">اللاعب غير موجود</div>;
  }
  if (!u) return <div className="text-white/40 text-center py-12 text-xs">جارٍ التحميل...</div>;

  const isOwner = !!currentUser?.id && currentUser.id === u.id;
  const isPlus = !!(u.is_personal_plus || u.is_plus || u.plan === "plus");
  const accent = (isPlus && u.accent_color) || "#FFCC00";
  const bannerStyle = isPlus && u.banner
    ? { backgroundImage: `url(${u.banner})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { backgroundColor: accent };
  const initial = (u.username?.[0] || "?").toUpperCase();
  const wins = u.wins || 0;
  const losses = u.losses || 0;
  const kd = u.kd != null ? u.kd : (losses === 0 ? wins.toFixed(2) : (wins / losses).toFixed(2));

  // دالة نسخ Game ID
  const handleCopyGameId = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  // دالة عرض وسام منصة اللعب (شاملة لجميع الاحتمالات)
  const renderPlatformBadge = (plat) => {
    const platformVal = plat || u.gaming_platform || u.platform;
    if (!platformVal) return null;
    
    const p = String(platformVal).toLowerCase();

    if (p.includes("ps") || p.includes("playstation")) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-xs font-bold bg-[#003791]/30 border border-[#003791]/60 text-[#0070d1]" title="PlayStation">
          <FaPlaystation size={12} /> PlayStation
        </span>
      );
    }
    if (p.includes("xbox")) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-xs font-bold bg-[#107c41]/30 border border-[#107c41]/60 text-[#107c41]" title="Xbox">
          <FaXbox size={12} /> Xbox
        </span>
      );
    }
    if (p.includes("pc") || p.includes("computer") || p.includes("steam")) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-xs font-bold bg-purple-500/10 border border-purple-500/30 text-purple-300" title="PC">
          <FaDesktop size={12} /> PC
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-xs font-bold bg-white/5 border border-white/10 text-white/70">
        {platformVal.toUpperCase()}
      </span>
    );
  };
  
  // 1. الأوسمة والإنجازات المكتسبة من البطولات والدوريات
  const dynamicBadges = (u.achievements || []).map((badge) => ({
    id: badge.id || badge.tournament_id,
    icon: <Trophy size={14} />,
    label: badge.label || badge.title || "بطل بطولة",
    value: 1,
    max: 1,
    tone: badge.kind === "tournament_gold" ? "gold" : "silver",
    isDynamic: true,
  }));

  // 2. الإحصائيات الفردية الأساسية
  const staticBadges = [
    { id: "attendance", icon: <Check size={14} />, label: "عدد مرات التحضير", value: u.attendances || 0, max: Math.max(10, u.attendances || 1), tone: "silver" },
    { id: "mvp", icon: <Trophy size={14} />, label: "نجم المباراة", value: u.mvp_count || 0, max: Math.max(5, u.mvp_count || 1), tone: "gold" },
    { id: "wins", icon: <Swords size={14} />, label: "Wins", value: wins, max: Math.max(10, wins), tone: "bronze" },
    { id: "losses", icon: <Flame size={14} />, label: "Losses", value: losses, max: Math.max(10, losses), tone: "danger" },
  ];

  // دمج كافة الإنجازات
  const ALL_ACHIEVEMENTS = [...dynamicBadges, ...staticBadges];

  // حفظ تفضيلات الإخفاء
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/users/me/achievements-settings', {
        hidden_achievements: hiddenIds,
        featured_achievements: []
      });
      setIsEditing(false);
    } catch (err) {
      console.error("فشل حفظ إعدادات الإنجازات", err);
    } finally {
      setSaving(false);
    }
  };

  // تبديل إخفاء الإنجاز
  const toggleHide = (badgeId) => {
    if (hiddenIds.includes(badgeId)) {
      setHiddenIds(hiddenIds.filter((item) => item !== badgeId));
    } else {
      setHiddenIds([...hiddenIds, badgeId]);
    }
  };

  // تصفية الإنجازات للعرض
  const displayedAchievements = ALL_ACHIEVEMENTS.filter((badge) => {
    if (!isEditing && hiddenIds.includes(badge.id)) return false;
    if (filterMode === "unlocked") {
      return badge.isDynamic ? true : Number(badge.value) > 0;
    }
    return true;
  });

  const gameId = u.activision_id || u.game_id || u.activision;

  const submitReport = async () => {
    if (reportMessage.trim().length < 5) return;
    setReportSending(true);
    try {
      await api.post(`/users/${u.id}/report`, { reason: reportReason, message: reportMessage.trim() });
      alert("تم إرسال البلاغ بسرية وسيتم مراجعته من الإدارة");
      setReportOpen(false); setReportMessage("");
    } catch (e) { alert(e.response?.data?.detail || "تعذر إرسال البلاغ"); }
    finally { setReportSending(false); }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4" dir="rtl">
      {currentUser && !isOwner && (
        <div className="flex justify-end">
          <button onClick={() => setReportOpen(true)} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/30 text-red-300 bg-red-500/5 hover:bg-red-500/10 text-xs font-bold"><Flag size={14}/> الإبلاغ عن اللاعب</button>
        </div>
      )}
      {reportOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-4">
          <div className="w-full max-w-lg rounded-2xl border border-white/15 bg-[#141417] p-6 space-y-4">
            <h2 className="text-xl font-black text-white">الإبلاغ عن اللاعب</h2>
            <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-3 text-xs text-emerald-100">بلاغك سري بالكامل عن اللاعب وبقية المستخدمين. سيتم التعامل معه بسرية تامة من قبل الإدارة، ولن يعرف اللاعب أنك قمت بالإبلاغ عنه.</div>
            <select value={reportReason} onChange={e=>setReportReason(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-white">
              <option>الغش</option><option>سلوك غير لائق</option><option>إساءة استخدام النظام</option><option>حساب مشبوه</option><option>سبب آخر</option>
            </select>
            <textarea value={reportMessage} onChange={e=>setReportMessage(e.target.value)} rows={5} placeholder="يرجى توضيح سبب البلاغ..." className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-white"/>
            <div className="flex gap-2 justify-end"><button onClick={()=>setReportOpen(false)} className="px-4 py-2 rounded-xl border border-white/10">إلغاء</button><button disabled={reportSending || reportMessage.trim().length<5} onClick={submitReport} className="px-4 py-2 rounded-xl bg-red-600 text-white font-bold disabled:opacity-40">{reportSending ? "جاري الإرسال..." : "إرسال البلاغ"}</button></div>
          </div>
        </div>
      )}
      <div data-testid="player-profile" className="rounded-3xl overflow-hidden border border-white/10 bg-[#111113]/90 shadow-2xl">
        <div className="h-44 md:h-52 w-full relative" style={bannerStyle}>
          <div className="absolute inset-0 bg-gradient-to-t from-[#111113] via-[#111113]/40 to-transparent" />
          {isPlus && (
            <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-[10px] uppercase font-black tracking-widest bg-black/60 backdrop-blur-md px-3 py-1 rounded-xl border border-gold-500/30 text-gold-400 shadow-lg">
              <Sparkles size={11} className="text-gold-400 drop-shadow-[0_0_8px_rgba(212,175,55,0.7)]" />
              <span>Personal Plus</span>
            </div>
          )}
        </div>
        <div className="px-6 md:px-8 pb-6 -mt-14 relative">
          <div className="flex items-end gap-4 flex-wrap justify-between">
            <div className="flex items-end gap-4 flex-wrap">
             <div
                data-testid="player-avatar"
                className="h-24 w-24 md:h-28 md:w-28 rounded-full border-4 border-[#111113] grid place-items-center overflow-hidden text-3xl font-display font-black shadow-2xl shrink-0 transition-transform duration-300 hover:scale-105"
                style={{
                  backgroundColor: isPlus && u.avatar ? "transparent" : `${accent}22`,
                  color: accent,
                  boxShadow: `0 0 0 3px ${accent}55, 0 10px 25px -5px rgba(0, 0, 0, 0.5)`,
                }}
              >
                {isPlus && u.avatar ? <img src={u.avatar} alt={u.username} className="h-full w-full object-cover" /> : initial}
              </div>
              <div className="flex-1 min-w-0">
                {u.act ? (
                  <h1 data-testid="player-act" className="font-display font-black text-2xl sm:text-3xl md:text-4xl leading-tight break-all whitespace-normal" style={{ color: accent }}>{u.act}</h1>
                ) : (
                  <h1 className="font-display font-black text-2xl sm:text-3xl md:text-4xl leading-tight break-all whitespace-normal text-white">{u.username}</h1>
                )}
                
                {/* قسم اسم المستخدم + المنصة */}
                <div className="flex items-center gap-2.5 mt-1.5 flex-wrap">
                  <span className="text-white/50 text-xs font-bold">@{u.username}</span>
                  {renderPlatformBadge(u.gaming_platform || u.platform)}
                </div>

                {/* Game ID مع زر النسخ */}
                {!!gameId && (
                  <div className="mt-2.5 inline-flex items-center gap-2 bg-black/40 border border-white/10 rounded-xl px-3 py-1.5 text-xs shadow-inner">
                    <span className="text-white/40 font-bold">Game ID:</span>
                    <span className="text-white font-mono dir-ltr">{gameId}</span>
                    <button
                      onClick={() => handleCopyGameId(gameId)}
                      className="text-white/40 hover:text-white transition p-0.5 cursor-pointer"
                      title="نسخ المعرف"
                    >
                      {copiedId ? <CheckCheck size={13} className="text-emerald-400" /> : <Copy size={13} />}
                    </button>
                  </div>
                )}

                {!!u.discord_username && (
                  <div className="text-white/65 text-xs mt-2 flex items-center gap-1.5 font-mono" dir="ltr" data-testid="player-discord-username">
                    <FaDiscord size={13} color="#5865F2" /> @{String(u.discord_username).replace(/^@+/, "")}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4 bg-black/40 border border-white/10 rounded-2xl p-3 shadow-inner">
              <Stat label="نقاط" value={u.points || 0} accent={accent} testid="player-points" />
              <div className="h-8 w-px bg-white/10" />
              <Stat label="فوز / خسارة" value={`${wins} / ${losses}`} accent={accent} testid="player-wl" />
              <div className="h-8 w-px bg-white/10" />
              <Stat label="W/L Ratio" value={Number(kd).toFixed(2)} accent={accent} testid="player-kd" />
            </div>
          </div>

          <div className="mt-6 grid lg:grid-cols-[1.2fr_1fr] gap-5">
            {/* قسم الإنجازات الأنيق المرتب بنظام شريط التقدم */}
            <div className="bg-black/30 p-5 rounded-2xl border border-white/10 shadow-inner space-y-4">
              <div className="flex justify-between items-center">
                <div className="text-[10px] uppercase font-bold tracking-widest text-white/40">الإنجازات والأوسمة</div>
                
                <div className="flex items-center gap-2">
                  <div className="flex bg-black/50 p-0.5 rounded-xl border border-white/10 text-xs">
                    <button
                      onClick={() => setFilterMode("unlocked")}
                      className={`px-3 py-1 rounded-lg transition cursor-pointer ${filterMode === "unlocked" ? "bg-white/10 text-white font-black" : "text-white/40 hover:text-white"}`}
                    >
                      المحققة
                    </button>
                    <button
                      onClick={() => setFilterMode("all")}
                      className={`px-3 py-1 rounded-lg transition cursor-pointer ${filterMode === "all" ? "bg-white/10 text-white font-black" : "text-white/40 hover:text-white"}`}
                    >
                      الكل
                    </button>
                  </div>

                  {isOwner && (
                    <button
                      onClick={() => isEditing ? handleSaveSettings() : setIsEditing(true)}
                      disabled={saving}
                      className={`inline-flex items-center gap-1.5 text-xs px-3.5 py-1.5 rounded-xl font-bold transition shadow-md cursor-pointer ${
                        isEditing ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "bg-white/10 hover:bg-white/20 text-white/80"
                      }`}
                    >
                      {isEditing ? (
                        <>{saving ? "جاري الحفظ..." : <><Save size={13} /> حفظ التعديلات</>}</>
                      ) : (
                        <><Settings size={13} /> تخصيص</>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* العرض بأسلوب بطاقات منظمة تشمل شريط تقدم متدرج (Progress Bar) */}
              <div className="grid sm:grid-cols-2 gap-3" data-testid="player-achievements">
                {displayedAchievements.length === 0 ? (
                  <div className="col-span-full text-center py-8 text-xs text-white/30">لا توجد إنجازات معروضة حالياً</div>
                ) : (
                  displayedAchievements.map((badge) => {
                    const isHidden = hiddenIds.includes(badge.id);
                    const currentVal = Number(badge.value) || 0;
                    const maxVal = Number(badge.max) || Math.max(10, currentVal);
                    const percent = badge.isDynamic ? 100 : Math.min(100, (currentVal / maxVal) * 100);

                    return (
                      <AchievementBadge
                        key={badge.id}
                        icon={badge.icon}
                        label={badge.label}
                        value={badge.value}
                        percent={percent}
                        accent={accent}
                        tone={badge.tone}
                        isEditing={isEditing}
                        isHidden={isHidden}
                        onToggleHide={() => toggleHide(badge.id)}
                      />
                    );
                  })
                )}
              </div>
            </div>

            {/* قسم التواصل الاجتماعي */}
            <div className="bg-black/30 p-5 rounded-2xl border border-white/10 shadow-inner flex flex-col justify-between space-y-4">
              <div>
                <div className="text-[10px] uppercase font-bold tracking-widest text-white/40 mb-3">القنوات والحسابات الاجتماعية</div>
                <div className="space-y-2">
                  {SOCIAL_PLATFORMS.map((p) => (
                    <SocialRow key={p.key} platform={p} url={u?.[p.key] || ""} />
                  ))}
                </div>
              </div>

              {u.clan_id && (
                <Link to={`/clans/${u.clan_id}`} className="inline-flex items-center gap-2 text-xs font-bold text-gold-400 hover:text-gold-300 transition">
                  <Shield size={15} /> انتقال لصفحة الكلان <ArrowRight size={13} />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {u.role !== "player" && (
        <div className="bg-[#111113]/90 border border-white/10 rounded-2xl p-4 flex items-center gap-3 text-xs shadow-xl">
          <Trophy size={16} className="text-gold-500 shrink-0" />
          <span className="text-white/60">الدور الإداري في المنصة:</span>
          <span className="font-bold text-gold-400 uppercase tracking-wider">{u.role === "owner" ? "مالك المنصة (Owner)" : "منظم رسمي (Organizer)"}</span>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="text-center px-1" data-testid={testid}>
      <div className="font-display font-black text-xl md:text-2xl" style={{ color: accent }}>{value}</div>
      <div className="text-[10px] uppercase font-bold tracking-widest text-white/40 mt-0.5">{label}</div>
    </div>
  );
}

function SocialRow({ platform, url }) {
  const { Icon, label, color, testid } = platform;
  const iconNode = <Icon size={16} color={color} />;
  if (!url) {
    return (
      <div className="w-full flex items-center gap-3 bg-black/40 border border-white/5 rounded-xl px-3 py-2 text-xs text-white/30" data-testid={testid}>
        <span>{iconNode}</span>
        <span className="font-bold w-16 shrink-0">{label}</span>
        <span className="text-[11px]">— غير مرتبط —</span>
      </div>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={testid} className="w-full flex items-center gap-3 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-xs hover:border-white/25 hover:bg-black/60 transition group">
      <span className="shrink-0">{iconNode}</span>
      <span className="font-bold w-16 shrink-0 text-white">{label}</span>
      <span className="text-white/70 truncate flex-1 group-hover:text-white">{url}</span>
      <span className="text-gold-400 text-[11px] font-bold">فتح ↗</span>
    </a>
  );
}

// ── بطاقة إنجاز مرتبة تتضمن شريط التقدم (Progress Bar) ──
function AchievementBadge({ icon, label, value, percent, accent, tone = "gold", isEditing, isHidden, onToggleHide }) {
  const toneClass = tone === "gold"
    ? "border-gold-500/30 bg-gold-500/10"
    : tone === "silver"
      ? "border-gray-300/30 bg-gray-300/10"
      : tone === "bronze"
        ? "border-amber-600/40 bg-amber-600/10"
        : "border-destructive/35 bg-destructive/10";
  const iconClass = tone === "gold"
    ? "text-gold-400"
    : tone === "silver"
      ? "text-gray-300"
      : tone === "bronze"
        ? "text-amber-500"
        : "text-destructive";

  return (
    <div className={`relative rounded-2xl border p-4 transition-all flex flex-col justify-between space-y-3 ${toneClass} ${
      isHidden && isEditing ? "opacity-30 border-dashed border-red-500/60" : ""
    }`}>
      {/* زر التبديل عند وضع التخصيص */}
      {isEditing && (
        <button
          onClick={onToggleHide}
          className={`absolute -top-2 -right-2 p-1.5 rounded-full text-[10px] shadow-lg transition cursor-pointer ${
            isHidden ? "bg-red-600 text-white" : "bg-black/80 text-gray-300 hover:text-white border border-white/20"
          }`}
          title={isHidden ? "إظهار الإنجاز" : "إخفاء الإنجاز"}
        >
          <EyeOff size={12} />
        </button>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center justify-center h-7 w-7 rounded-xl bg-black/40 border border-white/10 shrink-0">
            <span className={iconClass}>{icon}</span>
          </div>
          <span className="text-xs font-bold text-white truncate max-w-[130px]" title={label}>{label}</span>
        </div>
        <span className="text-sm font-display font-black" style={{ color: accent }}>{value}</span>
      </div>

      {/* شريط التقدم ونسبة الإنجاز */}
      <div className="space-y-1 pt-1 border-t border-white/5">
        <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${percent}%`, background: accent }}
          />
        </div>
   
      </div>
  
    </div>
  );
}
